// src/pages/OrderSuccessPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import { showToast } from '../utils/helpers';
import HeaderLanding from '../components/partials/HeaderLanding';
import DownloadModal from '../components/modals/DowloadModal';

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [orderData, setOrderData] = useState(null);
  const [digitalProducts, setDigitalProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (orderId) {
      fetchOrderSuccessData();
    }
  }, [orderId]);

  const fetchOrderSuccessData = async () => {
    try {
      const response = await api.get(`${API_CONFIG.ORDERS.ORDER_SUCCESS}/${orderId}/success`);
      if (response.data.success) {
        setOrderData(response.data.order);
        setDigitalProducts(response.data.digitalProducts || []);
      } else {
        setError('Order not found');
      }
    } catch (error) {
      console.error('Error fetching order data:', error);
      setError('Failed to load order details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownloadClick = (product) => {
    setSelectedProduct(product);
    setShowDownloadModal(true);
  };

  const handleDownloadComplete = (productId) => {
    // Update the download count in the UI if needed
    setDigitalProducts(prev => 
      prev.map(product => 
        product.productId === productId 
          ? { ...product, downloadsUsed: (product.downloadsUsed || 0) + 1 }
          : product
      )
    );
    showToast('Download completed successfully!', 'success');
  };

  const formatCurrency = (amount, currency = 'NGN') => {
    if (!amount) return currency === 'NGN' ? '₦0.00' : '$0.00';
    try {
      const options = {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      };
      return new Intl.NumberFormat(currency === 'NGN' ? 'en-NG' : 'en-US', options).format(amount);
    } catch (error) {
      return currency === 'NGN' ? `₦${amount.toFixed(2)}` : `$${amount.toFixed(2)}`;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your order details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <h1 className="text-2xl font-bold  mb-2">Order Not Found</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <HeaderLanding />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Success Header */}
        <div className="bg-white rounded-xl shadow-sm p-8 text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-3xl">✅</span>
          </div>
          <h1 className="text-3xl font-bold  mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">
            Thank you for your order <span className="font-mono font-semibold">#{orderId}</span>
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700"
            >
              Continue Shopping
            </button>
            <button 
              onClick={() => navigate(`/track/${orderId}`)}
              className="border border-blue-600 text-blue-600 px-6 py-3 rounded-lg hover:bg-blue-50"
            >
              Track Order
            </button>
          </div>
        </div>

        {/* Digital Products Section */}
        {digitalProducts.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 mb-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">📥</span>
              </div>
              <h2 className="text-2xl font-bold  mb-2">Your Digital Products Are Ready!</h2>
              <p className="text-gray-600">Click download and enter the PIN sent to your email</p>
            </div>

            <div className="space-y-6">
              {digitalProducts.map((product) => (
                <div key={product.productId} className="border border-blue-200 rounded-xl p-6">
                  <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Product Info */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        {product.productImage && (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
                            <img 
                              src={product.productImage} 
                              alt={product.productName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold  mb-2">
                            {product.productName}
                          </h3>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-3">
                            {product.fileSize && <span>Size: {product.fileSize}</span>}
                            {product.fileType && <span>Format: {product.fileType.toUpperCase()}</span>}
                            {product.maxDownloads && (
                              <span>Downloads: {product.downloadsUsed || 0}/{product.maxDownloads}</span>
                            )}
                          </div>
                          
                          {/* PIN Instructions */}
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center">
                              <svg className="w-5 h-5 text-blue-600 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                              </svg>
                              <div>
                                <p className="text-sm font-medium text-blue-800 mb-1">Download Protected</p>
                                <p className="text-xs text-blue-600">
                                  A 6-digit PIN has been sent to your email. Click download to enter it.
                                </p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Download Button */}
                    <button
                      onClick={() => handleDownloadClick(product)}
                      className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 whitespace-nowrap flex items-center transition-colors"
                    >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                      </svg>
                      Download Now
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Order Summary */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <h3 className="text-xl font-bold  mb-6">Order Summary</h3>
          
          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Customer Information</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Name:</span> {orderData.customerName}</p>
                <p><span className="font-medium">Email:</span> {orderData.customerEmail}</p>
                {orderData.customerPhone && <p><span className="font-medium">Phone:</span> {orderData.customerPhone}</p>}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">Order Details</h4>
              <div className="space-y-2 text-sm">
                <p><span className="font-medium">Order ID:</span> {orderId}</p>
                <p><span className="font-medium">Order Date:</span> {new Date(orderData.createdAt).toLocaleDateString()}</p>
                <p><span className="font-medium">Total Amount:</span> {formatCurrency(orderData.totalAmount)}</p>
              </div>
            </div>
          </div>

          {/* Support Section */}
          <div className="border-t pt-6">
            <h4 className="font-semibold text-gray-700 mb-3">Need Help?</h4>
            <p className="text-gray-600 text-sm mb-4">
              If you have any questions about your order or didn't receive your download PIN, contact our support team.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href={`mailto:support@paymeo.co?subject=Order ${orderId} - Download PIN`}
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                Email Support
              </a>
              <a 
                href="/contact"
                className="text-blue-600 hover:text-blue-700 text-sm"
              >
                Contact Form
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Download Modal */}
      <DownloadModal
        show={showDownloadModal}
        onHide={() => {
          setShowDownloadModal(false);
          setSelectedProduct(null);
        }}
        orderId={orderId}
        productId={selectedProduct?.productId}
        productName={selectedProduct?.productName}
        productImage={selectedProduct?.productImage}
        onDownloadComplete={() => handleDownloadComplete(selectedProduct?.productId)}
      />
    </div>
  );
};

export default OrderSuccessPage;